import type { SplitOrderLinePart, SplitOrderLineResponse } from '@/infrastructure/FulfillmentAdminClient';
import { LINE_SIZE_MAX_LENGTH } from '@/utils/fulfillmentLineEdit';

/** One part of the split as typed, held as text so a half-typed number is not a value yet. */
export interface LineSplitPartDraft {
  quantity: string;
  amount: string;
  trackingNumber: string;
  /** The part's display size (SSD-7514), opened as the line's; only a changed size travels. */
  size: string;
  /** On an arrived line, whether this part's units are in the warehouse; ignored on a line still awaiting its goods. */
  arrived: boolean;
}

/** The typed fields of a part; `arrived` is a choice, not text. */
export type LineSplitPartField = Exclude<keyof LineSplitPartDraft, 'arrived'>;

export type LineSplitPartErrors = Partial<Record<LineSplitPartField, string>>;

/** What a split is read from: the line as the order detail shows it. */
export interface SplittableLine {
  lineNo: number;
  totalQuantity: number;
  lineStatus: string;
  /** The line's sale total; null when the line records none, and then it splits by quantity only. */
  total?: number | null;
  lineTrackingNumber?: string | null;
  /** The line's display size; each part keeps it unless the operator retypes it (SSD-7514). */
  size?: string | null;
}

/** The server's caps on a split (SSD-7377). */
export const LINE_SPLIT_MIN_PARTS = 2;
export const LINE_SPLIT_MAX_PARTS = 20;
const TRACKING_NUMBER_MAX_LENGTH = 128;

/**
 * A line whose units are on a shelf: inbounded, or READY once every line of the order arrived. Its split says per part
 * whether those units arrived (SSD-7426).
 */
const ARRIVED_SPLITTABLE_STATUSES = ['INBOUNDED', 'READY'];

export const lineSplitTracksArrival = (line: Pick<SplittableLine, 'lineStatus'>): boolean =>
  ARRIVED_SPLITTABLE_STATUSES.includes(line.lineStatus);

/**
 * A line waiting for its goods, or arrived and not yet outbounded (SSD-7426). Once outbounded its units are in a
 * parcel and cannot move to another line.
 */
const SPLITTABLE_LINE_STATUSES = new Set(['ORDERED', 'RESERVED', 'AWAITING_INBOUND', ...ARRIVED_SPLITTABLE_STATUSES]);

export const LINE_SPLIT_MESSAGES = {
  quantity: 'Quantity must be a whole number above zero.',
  amount: 'Amount must be a number, 0 or more, with up to 2 decimals.',
  amountMissing: 'Enter an amount on every part, or leave every amount blank.',
  trackingNumber: `Tracking number can be up to ${TRACKING_NUMBER_MAX_LENGTH} characters.`,
  trackingNumberDuplicate: 'Another part already has this tracking number.',
  size: `Size can be up to ${LINE_SIZE_MAX_LENGTH} characters.`,
  sizeMissing: 'Enter a size for this part.',
  noneArrived:
    'Mark at least one part as arrived. To send the whole line back to awaiting inbound, cancel its inbound instead.',
} as const;

const MONEY = /^\d+(\.\d{1,2})?$/;

/** Cents of a typed amount, or undefined when the text is not one; whole cents keep the sums exact. */
const centsOf = (text: string): number | undefined =>
  MONEY.test(text.trim()) ? Math.round(Number(text.trim()) * 100) : undefined;

/** Why the split action is shut on this line, or '' when it is open. */
export const describeLineSplitBlock = (line: Pick<SplittableLine, 'lineStatus' | 'totalQuantity'>): string => {
  if (!SPLITTABLE_LINE_STATUSES.has(line.lineStatus)) {
    return 'Only a line not yet outbounded (ordered, reserved, awaiting inbound, inbounded or ready) can be split';
  }
  return line.totalQuantity < LINE_SPLIT_MIN_PARTS ? 'A line of one unit cannot be split' : '';
};

export const EMPTY_LINE_SPLIT_PART: LineSplitPartDraft = {
  quantity: '',
  amount: '',
  trackingNumber: '',
  size: '',
  arrived: false,
};

/** A part added to the split: blank but for the line's size, which every part keeps unless retyped. */
export const newLineSplitPart = (line: SplittableLine): LineSplitPartDraft => ({
  ...EMPTY_LINE_SPLIT_PART,
  size: line.size ?? '',
});

/**
 * The parts the dialog opens with: two, the first keeping the waybill the line already names. On an arrived line
 * the first part is the parcel that was scanned, so it starts arrived and the other starts awaiting its own scan.
 */
export const initialLineSplitParts = (line: SplittableLine): LineSplitPartDraft[] => [
  { ...newLineSplitPart(line), trackingNumber: line.lineTrackingNumber ?? '', arrived: true },
  newLineSplitPart(line),
];

export interface LineSplitReview {
  /** Per part, the sentence standing against each field that has one. */
  errors: LineSplitPartErrors[];
  /** Units of the line no part holds yet; negative when the parts hold more than the line. */
  remainingQuantity: number;
  /** Whether amounts are being split by hand; false leaves the server to share the money by quantity. */
  amountsNamed: boolean;
  /** Line total no part holds yet, in cents; undefined while amounts are not named or the line has no total. */
  remainingAmountCents: number | undefined;
  /** Why the split cannot be sent yet, or '' when it can. */
  block: string;
  /** The request parts, present only when nothing blocks the split. */
  parts: SplitOrderLinePart[] | undefined;
}

const filled = (text: string): boolean => text.trim() !== '';

/** Checks the typed parts against the line and builds the request; the server applies the same rules. */
export const reviewLineSplit = (line: SplittableLine, drafts: readonly LineSplitPartDraft[]): LineSplitReview => {
  const lineTotalCents = typeof line.total === 'number' ? Math.round(line.total * 100) : undefined;
  const amountsNamed = lineTotalCents !== undefined && drafts.some((draft) => filled(draft.amount));
  const tracksArrival = lineSplitTracksArrival(line);
  const lineSize = (line.size ?? '').trim();

  let quantitySum = 0;
  let amountCents = 0;
  // A part still waiting for its quantity is not wrong yet; it only holds the split.
  let quantityMissing = false;
  const trackingNumberCounts = new Map<string, number>();
  for (const draft of drafts) {
    const trackingNumber = draft.trackingNumber.trim();
    if (trackingNumber) {
      trackingNumberCounts.set(trackingNumber, (trackingNumberCounts.get(trackingNumber) ?? 0) + 1);
    }
  }
  const errors = drafts.map((draft) => {
    const partErrors: LineSplitPartErrors = {};
    const quantity = Number(draft.quantity.trim());
    if (!filled(draft.quantity)) {
      quantityMissing = true;
    } else if (Number.isInteger(quantity) && quantity >= 1) {
      quantitySum += quantity;
    } else {
      partErrors.quantity = LINE_SPLIT_MESSAGES.quantity;
    }
    if (amountsNamed) {
      const cents = centsOf(draft.amount);
      if (cents === undefined) {
        partErrors.amount = filled(draft.amount) ? LINE_SPLIT_MESSAGES.amount : LINE_SPLIT_MESSAGES.amountMissing;
      } else {
        amountCents += cents;
      }
    }
    const trackingNumber = draft.trackingNumber.trim();
    // A blank tracking number is fine: the part is recorded without a waybill until one is known.
    if (trackingNumber.length > TRACKING_NUMBER_MAX_LENGTH) {
      partErrors.trackingNumber = LINE_SPLIT_MESSAGES.trackingNumber;
    } else if ((trackingNumberCounts.get(trackingNumber) ?? 0) > 1) {
      partErrors.trackingNumber = LINE_SPLIT_MESSAGES.trackingNumberDuplicate;
    }
    const size = draft.size.trim();
    // A line with a size cannot hand a part none; a line without one may leave its parts without one too.
    if (size.length > LINE_SIZE_MAX_LENGTH) {
      partErrors.size = LINE_SPLIT_MESSAGES.size;
    } else if (size === '' && lineSize !== '') {
      partErrors.size = LINE_SPLIT_MESSAGES.sizeMissing;
    }
    return partErrors;
  });

  const remainingQuantity = line.totalQuantity - quantitySum;
  const remainingAmountCents = amountsNamed ? lineTotalCents! - amountCents : undefined;
  const hasFieldErrors = errors.some((partErrors) => Object.keys(partErrors).length > 0);

  let block = '';
  if (drafts.length < LINE_SPLIT_MIN_PARTS) {
    block = `A split needs at least ${LINE_SPLIT_MIN_PARTS} parts.`;
  } else if (drafts.length > LINE_SPLIT_MAX_PARTS) {
    block = `A split takes at most ${LINE_SPLIT_MAX_PARTS} parts.`;
  } else if (hasFieldErrors) {
    block = 'Fix the marked fields first.';
  } else if (quantityMissing) {
    block = 'Enter a quantity on every part.';
  } else if (remainingQuantity !== 0) {
    block = `The parts must hold the line's ${line.totalQuantity} units exactly.`;
  } else if (remainingAmountCents !== undefined && remainingAmountCents !== 0) {
    block = "The amounts must add up to the line's total exactly, so the order's totals stay as paid.";
  } else if (tracksArrival && !drafts.some((draft) => draft.arrived)) {
    block = LINE_SPLIT_MESSAGES.noneArrived;
  }

  return {
    errors,
    remainingQuantity,
    amountsNamed,
    remainingAmountCents,
    block,
    parts:
      block === ''
        ? drafts.map((draft) => {
            const trackingNumber = draft.trackingNumber.trim();
            const size = draft.size.trim();
            return {
              quantity: Number(draft.quantity.trim()),
              ...(amountsNamed ? { amount: centsOf(draft.amount)! / 100 } : {}),
              ...(trackingNumber ? { trackingNumber } : {}),
              // A part that keeps the line's size leaves it out, which is what a split always did.
              ...(size !== lineSize ? { size } : {}),
              // Only an arrived line names arrival, so a split of a waiting line asks the server what it always did.
              ...(tracksArrival ? { arrived: draft.arrived } : {}),
            };
          })
        : undefined,
  };
};

/** A cent figure as the order's currency writes it. */
export const formatCents = (cents: number): string => (cents / 100).toFixed(2);

/**
 * Each part's amount as the line's total shared by the parts' quantities, or undefined while the quantities do not
 * hold the line's units or the line records no total. Mirrors Facade's LineAmountSplitter in cents: each share is
 * rounded and the last part takes the rounding remainder, so the amounts add up to the total exactly.
 */
export const amountsByQuantity = (
  line: SplittableLine,
  drafts: readonly LineSplitPartDraft[],
): string[] | undefined => {
  if (typeof line.total !== 'number' || drafts.length < LINE_SPLIT_MIN_PARTS) {
    return undefined;
  }
  const quantities = drafts.map((draft) => (filled(draft.quantity) ? Number(draft.quantity.trim()) : NaN));
  if (!quantities.every((quantity) => Number.isInteger(quantity) && quantity >= 1)) {
    return undefined;
  }
  const totalQuantity = quantities.reduce((sum, quantity) => sum + quantity, 0);
  if (totalQuantity !== line.totalQuantity) {
    return undefined;
  }
  const totalCents = Math.round(line.total * 100);
  let assigned = 0;
  return quantities.map((quantity, index) => {
    const cents =
      index === quantities.length - 1 ? totalCents - assigned : Math.round((totalCents * quantity) / totalQuantity);
    assigned += cents;
    return formatCents(cents);
  });
};

/**
 * A refused split in the Order team's words. Refusals the server words plainly keep its sentence; the rest say what
 * to do next. Undefined leaves the caller's default wording.
 */
export const describeLineSplitRefusal = (
  lineNo: number,
  code: string | undefined,
  message: string | string[] | undefined,
): string | undefined => {
  switch (code) {
    case 'SPLIT_QUANTITY_MISMATCH':
      return `Line ${lineNo} changed since this screen loaded: the parts no longer hold its units. The order was reloaded; check the parts and split again.`;
    case 'SPLIT_AMOUNT_MISMATCH':
      return `Line ${lineNo} changed since this screen loaded: the amounts no longer add up to its total. The order was reloaded; check the amounts and split again.`;
    case 'LINE_STATES_NO_AMOUNT':
      return `Line ${lineNo} records no amount, so it splits by quantity only. Clear every amount and split again.`;
    case 'LINE_NOT_SPLITTABLE':
      return `${typeof message === 'string' ? message : `Line ${lineNo} can no longer be split`}. The order was reloaded.`;
    case 'SPLIT_ARRIVAL_REQUIRED':
      return `Line ${lineNo} was inbounded since this screen loaded. The order was reloaded; say which parts arrived and split again.`;
    case 'SPLIT_ARRIVAL_NOT_INBOUNDED':
      return `Line ${lineNo} is no longer inbounded: its inbound was cancelled since this screen loaded. The order was reloaded; split it again.`;
    case 'LINE_SPLIT_NEEDS_MYSQL':
      return 'Line split is not switched on for this server yet. Ask engineering to enable it.';
    default: {
      const sentences = [message ?? []].flat();
      // A server from before SSD-7426 refuses the arrival every part of an arrived line names.
      if (sentences.some((sentence) => /property arrived should not exist/.test(sentence))) {
        return `Splitting an arrived (inbounded) line is not live on this server yet, so line ${lineNo} was left as it was. Ask engineering when it ships.`;
      }
      // A server from before SSD-7514 refuses a retyped part size.
      return sentences.some((sentence) => /property size should not exist/.test(sentence))
        ? `Changing a part's size is not live on this server yet, so line ${lineNo} was left as it was. Keep the line's size on every part, or ask engineering when it ships.`
        : undefined;
    }
  }
};

/** Refusals that mean the line on screen is not the line the server holds, so the order is re-read. */
export const LINE_SPLIT_STALE_REFUSALS: ReadonlySet<string> = new Set([
  'SPLIT_QUANTITY_MISMATCH',
  'SPLIT_AMOUNT_MISMATCH',
  'LINE_STATES_NO_AMOUNT',
  'LINE_NOT_SPLITTABLE',
  'SPLIT_ARRIVAL_REQUIRED',
  'SPLIT_ARRIVAL_NOT_INBOUNDED',
]);

/** The success sentence of a split, naming the parts that still wait for their own inbound scan. */
export const describeLineSplitResult = (result: SplitOrderLineResponse): string => {
  const lineNos = result.parts.map((part) => part.lineNo);
  const awaiting = result.parts.filter((part) => part.arrived === false).map((part) => part.lineNo);
  const split = `Line ${result.lineNo} split into lines ${lineNos.join(', ')}.`;
  if (awaiting.length === 0) {
    return split;
  }
  return `${split} ${awaiting.length === 1 ? 'Line' : 'Lines'} ${awaiting.join(', ')} ${awaiting.length === 1 ? 'awaits' : 'await'} inbound.`;
};
